import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import {
  type FlowCreationPolicy,
  type FlowCreationPolicyResponse,
  type FlowEmailBranding,
  type FlowEmailConfig,
  type FlowEmailConfigUpdate,
  type FlowEmailConfigVerifyRequest,
  getFlowCreationPolicy,
  getFlowEmailBranding,
  getFlowEmailConfig,
  testFlowEmailConfig,
  updateFlowCreationPolicy,
  updateFlowEmailBranding,
  updateFlowEmailConfig,
  verifyFlowEmailConfig,
} from "@/api/admin-api"

import { GC_TIME, SLOW_STALE_TIME } from "./_shared"

export function useFlowCreationPolicyQuery(enabled = true) {
  return useQuery<FlowCreationPolicyResponse>({
    queryKey: ["admin", "flow-creation-policy"],
    queryFn: getFlowCreationPolicy,
    enabled,
    staleTime: SLOW_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateFlowCreationPolicyMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (policy: FlowCreationPolicy) => updateFlowCreationPolicy(policy),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "flow-creation-policy"] })
    },
  })
}

export function useFlowEmailBrandingQuery(enabled = true) {
  return useQuery<FlowEmailBranding>({
    queryKey: ["admin", "flow-email-branding"],
    queryFn: getFlowEmailBranding,
    enabled,
    staleTime: SLOW_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateFlowEmailBrandingMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (branding: FlowEmailBranding) => updateFlowEmailBranding(branding),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "flow-email-branding"] })
    },
  })
}

export function useFlowEmailConfigQuery(enabled = true) {
  return useQuery<FlowEmailConfig>({
    queryKey: ["admin", "flow-email-config"],
    queryFn: getFlowEmailConfig,
    enabled,
    staleTime: SLOW_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateFlowEmailConfigMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (config: FlowEmailConfigUpdate) => updateFlowEmailConfig(config),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "flow-email-config"] })
    },
  })
}

export function useVerifyFlowEmailConfigMutation() {
  return useMutation({
    mutationFn: async (config: FlowEmailConfigVerifyRequest) => verifyFlowEmailConfig(config),
  })
}

export function useTestFlowEmailConfigMutation() {
  return useMutation({
    mutationFn: async (recipient: string) => testFlowEmailConfig(recipient),
  })
}
